import { useState } from 'react';
import {
  AlertDialog,
  DialogResult,
  InformationDialog
} from 'src/components/analytics';
import { Grid, Button, AlertColor } from '@mui/material';
import {
  useAppDispatch,
  useAppSelector,
  useDialogVisibilityHook
} from 'src/hooks';
import './scenario-manager.scss';
import { useNavigate } from 'react-router';
import ButtonWithSpinner from 'src/components/buttons/button-with-spinner';
import {
  archivedSelectedScenarios,
  deleteSelectedScenarios,
  finalizedSelectedScenarios,
  toggleShareScenarioDialogVisibility,
  unArchivedSelectedScenarios,
  unMarkSelectedFinalizedScenarios,
} from './scenario-manager-slice';
import {
  getScenarioCompMetricsBarCharts,
  handleScenarioManagerToComparison
} from '../scenario-comparison/store/scenario-comparison-slice';
import ShareScenarioDialog from './components/share-scenario-dialog/share-scenario-dialog';
import SnackbarComponent from '../innovation/components/snackbar/snackbar';
import { fetchScenarioDetails } from '../home/api/simulator-module-api';
import { setScenario } from '../home/store/simulator-home-slice';
import { getAccessConfigForScenario } from '../home/store/simulator-home-async-thunks';
import NoteDialogContainer from '../common/components/note/note-dialog-container';

interface IScenarioManagerFooterProps {
  selectedScenarios: { [key: string]: number | string }[];
}

export default function ScenarioManagerFooter({
  selectedScenarios
}: IScenarioManagerFooterProps) {
  const navigate = useNavigate();
  const appDispatch = useAppDispatch();

  const [isConfirmationDialogOpen, setConfirmationDialogOpen] = useState(false);

  const sourceId = useAppSelector((state) => state.user.source);
  const { isTargetEnabled } = useAppSelector((state) => state.scenarioComparison);
  const { selectedScenarioArrayWithOrder } = useAppSelector(
    (state) => state.pricingSimulator
  );
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [snackbarContent, setSnackbarContent] = useState<{
    snackbarText: string;
    snackbarType: AlertColor;
  }>({
    snackbarText: '',
    snackbarType: 'success'
  });

  const { openShareScenarioDialog } = useAppSelector(
    (state) => state.scenarioManager
  );

  const { isDialogOpen, handleDialogVisibilityToggle } =
    useDialogVisibilityHook();

  /* Function to get the selected scenarios id's */
  const getScenarioIds = (): number[] =>
    selectedScenarios.map((row) => Number(row.scenario_id));

  const handleLoadScenario = (): void => {
    if (selectedScenarios.length === 1) {
      const [selectedScenario] = selectedScenarios;
      fetchScenarioDetails(sourceId, selectedScenario?.scenario_id)
        .then((response) => response.data)
        .then((scenario) => {
          appDispatch(setScenario(scenario));
          navigate(`/srm/simulator/${scenario.pk_id}/planner`);
          appDispatch(
            getAccessConfigForScenario({
              scenarioId: scenario.pk_id,
              sourceId
            })
          );
        });
      // navigate(`/srm/simulator/${selectedScenario.scenario_id}/planner`);
    } else {
      handleDialogVisibilityToggle();
    }
  };

  const handleConfirmationDialogClose = (result: DialogResult) => {
    if (result === DialogResult.YES) {
      appDispatch(
        deleteSelectedScenarios({ scenarioIds: getScenarioIds(), sourceId })
      );
    }
    setConfirmationDialogOpen(false);
  };

  const handleDeleteScenario = (): void => {
    setConfirmationDialogOpen(true);
  };

  const handleUnmarkFinalizedEvent = () => {
    appDispatch(
      unMarkSelectedFinalizedScenarios({
        scenarioIds: getScenarioIds(),
        sourceId
      })
    );
  };

  const handleScenarioArchiveEvent = () => {
    appDispatch(
      archivedSelectedScenarios({ scenarioIds: getScenarioIds(), sourceId })
    );
  };

  const handleScenarioUnarchiveEvent = () => {
    appDispatch(
      unArchivedSelectedScenarios({ scenarioIds: getScenarioIds(), sourceId })
    );
  };

  const handleScenarioFinalEvent = () => {
    appDispatch(
      finalizedSelectedScenarios({ scenarioIds: getScenarioIds(), sourceId })
    );
  };

  const isDisableUnMarkFinalized = () =>
    !(
      selectedScenarios.length &&
      selectedScenarios.filter((row) => row.is_finalized).length ===
      selectedScenarios.length
    );

  const isDisableFinalized = () =>
    !(
      selectedScenarios.length &&
      selectedScenarios.filter((row) => !row.is_finalized).length ===
      selectedScenarios.length
    );

  const isArchivedScenario = () =>
    !(
      selectedScenarios.length &&
      selectedScenarios.filter((row) => !row.is_archived).length ===
      selectedScenarios.length
    );

  const isUnArchivedScenario = () =>
    !(
      selectedScenarios.length &&
      selectedScenarios.filter((row) => row.is_archived).length ===
      selectedScenarios.length
    );

  const handleScenarioComparisonClick = () => {
    appDispatch(
      handleScenarioManagerToComparison({ isScenarioManagerToComparison: true })
    );
    return appDispatch(
      getScenarioCompMetricsBarCharts({
        sourceId,
        scenariosWithOrderOfSelection: {
          scenarios: selectedScenarioArrayWithOrder
        },
        isTargetEnabled
      })
    ).then(() => {
      navigate('/srm/simulator/scenario-comparison');
    });
  };




  return (
    <Grid
      container
      sx={{
        position: 'sticky',
        bottom: 0,
        backgroundColor: (theme) => theme.palette.common.white,
         padding: (theme) => theme.spacing(1),
        zIndex: 2
      }}
    >
      <Grid container alignItems='center' item xs={8}>
        <Grid item xs='auto'>
          <Button
            sx={{ ml: 1 }}
            variant='outlined'
            disabled={selectedScenarios.length < 1}
            onClick={handleDeleteScenario}
          >
            Delete
          </Button>
          <Button
            sx={{ ml: 1 }}
            variant='outlined'
            disabled={isArchivedScenario()}
            onClick={handleScenarioArchiveEvent}
          >
            Move to Archive
          </Button>
          <Button
            sx={{ ml: 1 }}
            variant='outlined'
            disabled={isUnArchivedScenario()}
            onClick={handleScenarioUnarchiveEvent}
          >
            Move to Unarchive
          </Button>
          <Button
            sx={{ ml: 1 }}
            disabled={isDisableFinalized()}
            variant='outlined'
            onClick={handleScenarioFinalEvent}
          >
            {!isDisableFinalized() && (
              <img
                src='/static/icons/AddFinalizedFlag.svg'
                alt='finalized'
                style={{ marginRight: '6px', height: '16px' }}
              />
            )}
            {isDisableFinalized() && (
              <img
                src='/static/icons/disabledFlag.svg'
                alt='finalized'
                style={{ marginRight: '6px', height: '16px' }}
              />
            )}
            Mark as Final
          </Button>
          <Button
            sx={{ ml: 1 }}
            disabled={isDisableUnMarkFinalized()}
            variant='outlined'
            onClick={handleUnmarkFinalizedEvent}
          >
            {!isDisableUnMarkFinalized() && (
              <img
                src='/static/icons/AddFinalizedFlag.svg'
                alt='finalized'
                style={{ marginRight: '6px', height: '16px' }}
              />
            )}
            {isDisableUnMarkFinalized() && (
              <img
                src='/static/icons/disabledFlag.svg'
                alt='finalized'
                style={{ marginRight: '6px', height: '16px' }}
              />
            )}
            Unmark as Final
          </Button>
        </Grid>
      </Grid>
      <Grid
        p={1}
        item
        xs={4}
        container
        spacing={1}
        justifyContent='end'
        alignItems='center'
      >
        <Button
          sx={{ ml: 1 }}
          variant='contained'
          onClick={handleLoadScenario}
          disabled={
            selectedScenarios.length > 1 || selectedScenarios.length === 0
          }
        >
          Load Scenario
        </Button>
        <ButtonWithSpinner
          disabled={
            selectedScenarios.length < 2 || selectedScenarios.length > 5
          }
          onClick={handleScenarioComparisonClick}
        >
          Compare Scenarios
        </ButtonWithSpinner>
      </Grid>
      {openShareScenarioDialog ? (
        <ShareScenarioDialog
          isOpen={openShareScenarioDialog}
          onClose={(isShared) => {
            appDispatch(
              toggleShareScenarioDialogVisibility({
                visibility: false,
                scenario: null
              })
            );
            if (isShared) {
              setSnackbarContent({
                snackbarText: 'Scenario shared successfully',
                snackbarType: 'success'
              });
              setIsSnackbarOpen(true);
            }
          }}
        />
      ) : null}
         <NoteDialogContainer sourceId = {sourceId}
         isScenarioManager={true}/>
      <InformationDialog
        closeHandler={handleDialogVisibilityToggle}
        isDialogOpen={isDialogOpen}
        message='Please select only one scenario to load.'
        title='Alert'
      />
      <AlertDialog
        closeHandler={handleConfirmationDialogClose}
        isDialogOpen={isConfirmationDialogOpen}
        message='Are you sure you want to delete the selected scenarios?'
        title='Info'
      />
      <SnackbarComponent
        isSnackbarOpen={isSnackbarOpen}
        setSnackbar={setIsSnackbarOpen}
        snackbarText={snackbarContent.snackbarText}
        snackbarType={snackbarContent.snackbarType}
      />
    </Grid>
  );
}
